import { Rng } from './rng';

/**
 * Random streams, one per system.
 *
 * **Why separate streams.** If every system drew from one stream, the number of
 * draws one system took would decide what every later system drew, so a change
 * to the fishing fleet would reshuffle tomorrow's heatwave and move every
 * seeded measurement in the project. Sable Reserve found that out one system
 * at a time (its weather got its own stream first; its M132 found the game
 * drive reshuffling the ecology). Here it is the rule from the first commit.
 *
 * Two kinds, as there:
 *
 * - **Carried streams**: the state is a field on `World` and advances as it is
 *   drawn. `rngState` is the ecology's, `climateRngState` the ocean climate's.
 *   Each starts from `carriedStreamSeed(seed, salt)`.
 * - **Derived streams**: `dayStream(world, salt)`, a pure function of the
 *   world's seed, the day and a salt. No field on `World`, no position on any
 *   other stream, and a loaded save draws what it would have drawn. For any
 *   system whose number of draws depends on what the player has built.
 *   `stockStream(world, salt, place, island)` is one for each stock of a
 *   species (M80): the populations' draws depend on their numbers.
 *
 * Every salt lives in `STREAM`, so no two systems can share one by accident.
 * The values are fixed for good: changing one changes every seeded run.
 */
export const STREAM = {
  /** Carried: the ecology's, which nothing draws on since M80 (the fish take `stockStream`). ASCII 'ECOL'. */
  ecology: 0x45434f4c,
  /** Carried: ENSO, heatwaves, storms; Sable Reserve's weather stream. ASCII 'CLIM'. */
  climate: 0x434c494d,
  /** Derived, each stock's own (`stockStream`): the bullethead parrotfish's deaths and settlers, island by island (M80). ASCII 'BLTH'. */
  bullethead: 0x424c5448,
  /** Derived, each stock's own (`stockStream`): the palenose parrotfish's deaths and settlers, island by island (M82). ASCII 'PLNS'. */
  palenose: 0x504c4e53,
  /** Derived, each stock's own (`stockStream`): the peacock grouper's deaths and settlers, island by island (M84). ASCII 'ROIG'. */
  roi: 0x524f4947,
  /** Derived, each stock's own (`stockStream`): the bluespine unicornfish's (the kala's), island by island (M90). ASCII 'KALA'. */
  kala: 0x4b414c41,
  /** Derived, each stock's own (`stockStream`): the nenue's, the sea chubs', island by island (M178). ASCII 'NENU'. */
  nenue: 0x4e454e55,
  /** Derived, each stock's own (`stockStream`): the moano kea's, the blue goatfish's, island by island (M179). ASCII 'MOAN'. */
  moanokea: 0x4d4f414e,
  /** Derived, each stock's own (`stockStream`): the manini's, the convict tang's, island by island (M220). ASCII 'MANI'. */
  manini: 0x4d414e49,
  /** Derived, each stock's own (`stockStream`): the taʻape's, the bluestripe snapper's, island by island (M218). ASCII 'TAAP'. */
  taape: 0x54414150,
  /** Derived, each stock's own (`stockStream`): the giant trevally's (the ulua's), island by island (M126). ASCII 'ULUA'. */
  ulua: 0x554c5541,
  /** Derived, each stock's own (`stockStream`): the bluefin trevally's (the ʻōmilu's), island by island (M126). ASCII 'OMLU'. */
  omilu: 0x4f4d4c55,
  /** Derived, each stock's own (`stockStream`): the redlip parrotfish's, island by island (M128). ASCII 'SRUB'. */
  redlip: 0x53525542,
  /** Derived, each stock's own (`stockStream`): the day octopus's, island by island (M129). ASCII 'OCYA'. */
  octopus: 0x4f435941,
  /** Derived, each stock's own (`stockStream`): the green sea turtle's (the honu's) deaths, journeys and hatchlings, one stock (M93). ASCII 'HONU'. */
  honu: 0x484f4e55,
  /** Derived, each stock's own (`stockStream`): the hawksbill's (the honuʻea's) deaths, journeys and hatchlings, one stock (M187). ASCII 'HKSB'. */
  honuea: 0x484b5342,
  /** Derived, each stock's own (`stockStream`): the Deep 7 bottomfish's process error, one stock, drawn each 1 July (M192). ASCII 'DEP7'. */
  deep7: 0x44455037,
  /** Derived, each stock's own (`stockStream`): the humpbacks' deaths and calves, one population (M101). ASCII 'HUMP'. */
  humpback: 0x48554d50,
  /** Derived, each stock's own (`stockStream`): the monk seals' deaths and pups, one stock (M103). ASCII 'SEAL'. */
  monkSeal: 0x5345414c,
  /** Derived, each stock's own (`stockStream`): the tiger sharks' deaths and pups, one stock (M197). ASCII 'GCUV' (Galeocerdo cuvier). */
  tigerShark: 0x47435556,
  /** Derived, each stock's own (`stockStream`): the whitetip reef sharks' deaths and pups, one stock (M219). ASCII 'TOBE' (Triaenodon obesus). */
  whitetip: 0x544f4245,
  /** Derived, each stock's own (`stockStream`): the North Atlantic right whales' deaths and calves, one population, the North Atlantic's (M107). ASCII 'NARW'. */
  rightWhale: 0x4e415257,
  /** Derived, each stock's own (`stockStream`): the Pacific blue marlin's deaths and young, one stock, the Pacific's (M108). ASCII 'BUMP'. */
  blueMarlin: 0x42554d50,
  /** Derived, each stock's own (`stockStream`): the western Pacific leatherbacks' deaths, nesters and hatchlings, one population, the Pacific's (M111). ASCII 'DCOR' (Dermochelys coriacea). */
  leatherback: 0x44434f52,
  /** Derived, each stock's own (`stockStream`): the eastern North Pacific gray whales' deaths, strikes and calves, one population, the North Pacific's (M112). ASCII 'EROB' (Eschrichtius robustus). */
  grayWhale: 0x45524f42,
  /** Derived, each tagged animal's own (`tags.ts`): its class drawn as it is tagged and its fate each day, by its id and the day (M148). ASCII 'TAGS'. */
  tags: 0x54414753,
  /** Derived, each monitoring program's own (`monitoring.ts`): its survey's error, by its species and the day (M160). ASCII 'MNTR'. */
  monitoring: 0x4d4e5452,
  /** Derived, each commissioned survey's own (`operations.ts`): its counts' error, by the program and the survey's number and the day (M238). ASCII 'OPRS'. */
  operations: 0x4f505253,
} as const;

/**
 * The murmur3 32-bit finalizer: a bijection on 32-bit integers that spreads
 * every input bit over every output bit. Used to seed streams, so seeds that
 * differ in one low bit start unrelated sequences.
 */
export function mix32(x: number): number {
  let h = x >>> 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** The starting state of a carried stream. */
export function carriedStreamSeed(worldSeed: number, salt: number): number {
  return mix32(mix32(worldSeed) ^ salt);
}

/**
 * A derived stream for one system on one day: the same sequence for the same
 * `(seed, day, salt)`, whatever else has been drawn.
 */
export function dayStream(world: { seed: number; day: number }, salt: number): Rng {
  return new Rng(mix32(mix32(world.seed ^ salt) ^ Math.imul(world.day + 1, 0x9e3779b1)));
}

/** FNV-1a over a name's UTF-16 code units: a place's own number for its stocks' streams. */
function nameHash(name: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < name.length; i++) h = Math.imul(h ^ name.charCodeAt(i), 0x01000193);
  return h >>> 0;
}

/**
 * A derived stream for one stock of a species on one day (M80): its fish on
 * one island of one place, the same sequence for the same `(seed, day,
 * salt, place, island)` whatever any other stock or stream has drawn. A
 * population's draws depend on its numbers, which the player's actions will
 * move (Phase 5), so none draws on a carried stream (`docs/CODE_REVIEW.md`
 * section 7), and none on another's: one island's fish taking more draws
 * moves no other island's.
 */
export function stockStream(world: { seed: number; day: number }, salt: number, place: string, island: number): Rng {
  const stock = mix32(mix32(world.seed ^ salt) ^ nameHash(place)) ^ Math.imul(island + 1, 0x85ebca77);
  return new Rng(mix32(mix32(stock) ^ Math.imul(world.day + 1, 0x9e3779b1)));
}
